import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import GroupScheduleCalendar from '../ranking/components/GroupScheduleCalendar';
import MemberWeeklyTrendChart from '../ranking/components/MemberWeeklyTrendChart';
import AchievementList from '../home/components/AchievementList';
import AchievementForm from '../home/components/AchievementForm';
import { Link, useSearchParams } from 'react-router-dom';

interface StudyGroup {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  invite_code: string;
  member_count: number;
  is_member: boolean;
  created_at: string;
}

interface GroupSchedule {
  id: string;
  group_id: string;
  calendar_name: string;
  created_by: string;
  created_at: string;
}

interface GroupMember {
  user_id: string;
  username: string;
  avatar_url?: string;
  joined_at: string;
}

export default function GroupsPage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState<StudyGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<StudyGroup | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showJoinGroup, setShowJoinGroup] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [groupSchedules, setGroupSchedules] = useState<{ [groupId: string]: GroupSchedule }>({});
  const [showCreateSchedule, setShowCreateSchedule] = useState<string | null>(null);
  const [calendarName, setCalendarName] = useState('');
  const [openCalendarGroupId, setOpenCalendarGroupId] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [showMembersList, setShowMembersList] = useState(true);
  const [achievementRefreshKey, setAchievementRefreshKey] = useState(0);
  const achievementSectionRef = useRef<HTMLDivElement>(null);
  const urlHandledRef = useRef(false);

  const myGroups = groups.filter(g => g.is_member);

  useEffect(() => {
    if (!user) return;
    fetchGroups();
    fetchGroupSchedules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // URLパラメータによる自動グループ選択・カレンダーオープン処理
  useEffect(() => {
    if (urlHandledRef.current) return;
    if (groups.length === 0) return;

    const groupId = searchParams.get('groupId');
    const openCalendar = searchParams.get('openCalendar');
    const section = searchParams.get('section');

    if (!groupId) return;

    const targetGroup = groups.find(g => g.id === groupId && g.is_member);
    if (!targetGroup) return;

    urlHandledRef.current = true;
    setSelectedGroup(targetGroup);

    // カレンダー（チャット）を自動オープン
    if (openCalendar === 'true') {
      // groupSchedulesが読み込まれてからオープン
      setTimeout(() => {
        setOpenCalendarGroupId(groupId);
      }, 500);
    }

    // 達成報告セクションへスクロール
    if (section === 'achievements') {
      setTimeout(() => {
        achievementSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }

    // URLパラメータをクリア
    setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, searchParams]);

  const fetchGroupMembers = async (groupId: string) => {
    setLoadingMembers(true);
    try {
      const { data: memberships, error: memberError } = await supabase
        .from('study_group_members')
        .select('user_id, joined_at')
        .eq('group_id', groupId)
        .order('joined_at', { ascending: true });
      if (memberError) throw memberError;
      const userIds = memberships?.map(m => m.user_id) || [];
      if (userIds.length === 0) { setGroupMembers([]); return; }
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url')
        .in('user_id', userIds);
      const members: GroupMember[] = memberships.map(m => {
        const profile = profiles?.find(p => p.user_id === m.user_id);
        return { user_id: m.user_id, username: profile?.username || '不明なユーザー', avatar_url: profile?.avatar_url, joined_at: m.joined_at };
      });
      setGroupMembers(members);
    } catch (error) {
      console.error('グループメンバーの取得に失敗しました:', error);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (selectedGroup) fetchGroupMembers(selectedGroup.id);
  }, [selectedGroup]);

  const fetchGroups = async () => {
    if (!user) return;
    try {
      const { data: allGroups, error } = await supabase.from('study_groups').select('*');
      if (error) throw error;
      const { data: memberships } = await supabase.from('study_group_members').select('group_id').eq('user_id', user.id);
      const memberGroupIds = memberships?.map(m => m.group_id) || [];
      const groupsWithMembers = await Promise.all(
        (allGroups || []).map(async (group) => {
          const { count } = await supabase.from('study_group_members').select('*', { count: 'exact', head: true }).eq('group_id', group.id);
          return { ...group, member_count: count || 0, is_member: memberGroupIds.includes(group.id) };
        })
      );
      setGroups(groupsWithMembers);
    } catch (error) { console.error('Error fetching groups:', error); }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newGroupName.trim()) return;
    try {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const { data: newGroup, error } = await supabase.from('study_groups').insert({ name: newGroupName.trim(), description: newGroupDescription.trim(), created_by: user.id, invite_code: code }).select().single();
      if (error) throw error;
      await supabase.from('study_group_members').insert({ group_id: newGroup.id, user_id: user.id });
      setNewGroupName(''); setNewGroupDescription(''); setShowCreateGroup(false);
      fetchGroups();
    } catch (error) { console.error('Error creating group:', error); }
  };

  const joinGroup = async () => {
    if (!user || !inviteCode.trim()) return;
    try {
      const { data: group, error: groupError } = await supabase.from('study_groups').select('id').eq('invite_code', inviteCode.toUpperCase()).maybeSingle();
      if (groupError || !group) return;
      const { error } = await supabase.from('study_group_members').insert({ group_id: group.id, user_id: user.id });
      if (error) return;
      setInviteCode(''); setShowJoinGroup(false); fetchGroups();
    } catch (error) { console.error('Error joining group:', error); }
  };

  const leaveGroup = async (groupId: string) => {
    if (!user) return;
    try {
      await supabase.from('study_group_members').delete().eq('group_id', groupId).eq('user_id', user.id);
      if (selectedGroup?.id === groupId) setSelectedGroup(null);
      fetchGroups();
    } catch (error) { console.error('Error leaving group:', error); }
  };

  const fetchGroupSchedules = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.from('group_schedules').select('*');
      if (error) throw error;
      const map: { [groupId: string]: GroupSchedule } = {};
      (data || []).forEach((schedule: GroupSchedule) => { map[schedule.group_id] = schedule; });
      setGroupSchedules(map);
    } catch (error) { console.error('Error fetching group schedules:', error); }
  };

  const createSchedule = async (groupId: string) => {
    if (!user || !calendarName.trim()) return;
    try {
      const { data, error } = await supabase.from('group_schedules').insert({ group_id: groupId, calendar_name: calendarName.trim(), created_by: user.id }).select().single();
      if (error) throw error;
      setGroupSchedules(prev => ({ ...prev, [groupId]: data }));
      setCalendarName(''); setShowCreateSchedule(null); setOpenCalendarGroupId(groupId);
    } catch (error) { console.error('Error creating schedule:', error); }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />

      {authLoading ? (
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500/20 animate-ping"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-emerald-400 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center text-2xl">👥</div>
          </div>
        </div>
      ) : !user ? (
        <section className="pt-20 sm:pt-24 pb-16 relative overflow-hidden" style={{ minHeight: 'calc(100vh - 64px)', background: 'linear-gradient(135deg, #064e3b 0%, #065f46 25%, #047857 50%, #059669 75%, #10b981 100%)' }}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex flex-col items-center justify-center min-h-[calc(100vh-160px)] text-center">
            <div className="text-6xl sm:text-7xl mb-6">👥</div>
            <h1 className="text-3xl sm:text-5xl font-black text-white mb-3 tracking-tight">グループ機能</h1>
            <p className="text-teal-100 text-base sm:text-lg font-semibold mb-2">友達とグループを作って一緒に学習しよう！</p>
            <p className="text-white/60 text-sm mb-10">グループ機能はログインユーザー限定です</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/login" className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg flex items-center justify-center gap-2">
                <i className="ri-login-box-line text-lg"></i>ログイン
              </Link>
              <Link to="/login" className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm flex items-center justify-center gap-2">
                <i className="ri-user-add-line text-lg"></i>新規登録
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="pt-20 sm:pt-24 pb-10 sm:pb-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 25%, #047857 50%, #059669 75%, #10b981 100%)' }}>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-emerald-400/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-32 -left-20 w-[600px] h-[600px] bg-teal-400/8 rounded-full blur-3xl"></div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
              <nav className="flex items-center gap-2 text-xs sm:text-sm text-emerald-300/70 mb-5 sm:mb-8">
                <Link to="/" className="hover:text-white transition-colors">Top</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white font-medium">グループ</span>
              </nav>
              <div className="text-center">
                <div className="text-4xl sm:text-6xl mb-4 sm:mb-5">👥</div>
                <h1 className="text-2xl sm:text-5xl font-black text-white mb-2 sm:mb-3 tracking-tight">グループ機能</h1>
                <p className="text-emerald-200/80 text-sm sm:text-lg font-semibold mb-1 sm:mb-2">友達とグループを作って一緒に学習しよう！</p>
                <p className="text-white/40 text-xs sm:text-sm hidden sm:block">スケジュール共有、達成報告、週間学習推移などの機能が使えます</p>
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
            {!selectedGroup ? (
              <FadeInSection>
                <div className="space-y-5 sm:space-y-6">
                  {myGroups.length === 0 && (
                    <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-emerald-500/10 rounded-3xl border-2 border-emerald-500/20 p-5 sm:p-10 mb-6 sm:mb-8">
                      <div className="text-center mb-6 sm:mb-8">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-emerald-500/20 rounded-full mx-auto mb-4">
                          <i className="ri-group-line text-3xl sm:text-4xl text-emerald-400"></i>
                        </div>
                        <h2 className="text-xl sm:text-3xl font-bold text-white mb-3">グループで一緒に学習しよう！</h2>
                        <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto">友達とグループを作成して、スケジュールを共有したり、達成報告を共有したりできます。</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-500/20 rounded-xl">
                              <i className="ri-add-circle-line text-xl text-emerald-400"></i>
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white">グループを作成する</h3>
                          </div>
                          <button onClick={() => setShowCreateGroup(true)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold flex items-center justify-center gap-2 text-sm">
                            <i className="ri-add-line text-lg"></i>グループを作成
                          </button>
                        </div>
                        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-teal-500/20 rounded-xl">
                              <i className="ri-login-box-line text-xl text-teal-400"></i>
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white">招待コードで参加する</h3>
                          </div>
                          <button onClick={() => setShowJoinGroup(true)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-white/10 text-white border border-white/20 rounded-xl hover:bg-white/15 transition-all font-bold flex items-center justify-center gap-2 text-sm">
                            <i className="ri-login-box-line text-lg"></i>招待コードで参加
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h2 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2">
                        <i className="ri-group-line text-emerald-400"></i>参加中のグループ
                      </h2>
                      <div className="flex gap-2">
                        <button onClick={() => setShowJoinGroup(true)} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-white/5 text-emerald-300 border border-emerald-500/30 rounded-xl hover:bg-emerald-500/10 transition-all font-semibold whitespace-nowrap text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                          <i className="ri-login-box-line"></i>参加
                        </button>
                        <button onClick={() => setShowCreateGroup(true)} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 font-semibold whitespace-nowrap text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                          <i className="ri-add-line"></i>作成
                        </button>
                      </div>
                    </div>

                    {myGroups.length === 0 ? (
                      <div className="bg-white/5 rounded-3xl border border-white/10 p-8 sm:p-16 text-center">
                        <div className="text-5xl sm:text-7xl mb-5 animate-bounce" style={{ animationDuration: '2s' }}>👥</div>
                        <p className="text-base sm:text-xl text-white/50 mb-2 font-semibold">まだグループに参加していません</p>
                        <p className="text-white/30 text-sm mb-8">グループを作成または参加しましょう！</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                        {myGroups.map((group) => (
                          <div key={group.id} className="bg-white/5 rounded-2xl border border-white/10 p-4 sm:p-6 hover:bg-white/8 hover:border-emerald-500/30 transition-all duration-300 group/card">
                            <div className="flex items-start justify-between mb-3 sm:mb-4">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base sm:text-lg font-bold text-white mb-1 truncate group-hover/card:text-emerald-300 transition-colors">{group.name}</h3>
                                {group.description && <p className="text-xs sm:text-sm text-white/45 line-clamp-2">{group.description}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-white/35 mb-4 sm:mb-5 flex-wrap">
                              <span className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full">
                                <i className="ri-user-line text-emerald-400"></i>{group.member_count}人
                              </span>
                              <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full font-mono">
                                <i className="ri-key-line text-emerald-400"></i>
                                <span className="text-[11px]">{group.invite_code}</span>
                                <button onClick={() => copyInviteCode(group.invite_code)} className="ml-1 p-0.5 hover:bg-white/10 rounded transition-colors cursor-pointer" title="招待コードをコピー">
                                  <i className={`${copiedCode === group.invite_code ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line text-white/50'} text-xs`}></i>
                                </button>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => setSelectedGroup(group)} className="flex-1 min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold text-xs sm:text-sm shadow-md shadow-emerald-500/20 cursor-pointer">
                                詳細を見る
                              </button>
                              <button onClick={() => leaveGroup(group.id)} className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all cursor-pointer" title="退出">
                                <i className="ri-logout-box-line text-base sm:text-lg"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </FadeInSection>
            ) : (
              <FadeInSection>
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-4 sm:p-8 shadow-2xl">
                  {/* ヘッダー */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 sm:gap-3 mb-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-500/20 rounded-xl">
                          <i className="ri-group-line text-emerald-400 text-base sm:text-xl"></i>
                        </div>
                        <h2 className="text-xl sm:text-3xl font-bold text-white break-words">{selectedGroup.name}</h2>
                      </div>
                      {selectedGroup.description && <p className="text-xs sm:text-base text-white/45 break-words ml-10 sm:ml-13">{selectedGroup.description}</p>}
                    </div>
                    <button onClick={() => setSelectedGroup(null)} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap border border-white/10 self-start sm:self-auto cursor-pointer">
                      <i className="ri-arrow-left-line"></i>戻る
                    </button>
                  </div>

                  {/* グループメンバー */}
                  <div className="mb-6 sm:mb-8 bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                    <button onClick={() => setShowMembersList(prev => !prev)} className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/5 transition-colors cursor-pointer">
                      <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                        <i className="ri-team-line text-emerald-400"></i>
                        グループメンバー
                        <span className="text-xs sm:text-sm text-white/50 bg-white/10 px-2.5 py-0.5 rounded-full font-normal">{groupMembers.length}人</span>
                      </h3>
                      {showMembersList ? <i className="ri-arrow-up-s-line text-white/50 text-xl"></i> : <i className="ri-arrow-down-s-line text-white/50 text-xl"></i>}
                    </button>
                    {showMembersList && (
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                        {loadingMembers ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
                          </div>
                        ) : groupMembers.length === 0 ? (
                          <div className="text-center py-8">
                            <i className="ri-user-line text-4xl text-white/20 mb-2"></i>
                            <p className="text-white/40 text-sm">メンバーがいません</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {groupMembers.map((member) => {
                              const isMe = member.user_id === user?.id;
                              return (
                                <div key={member.user_id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isMe ? 'bg-gradient-to-r from-teal-500/20 to-emerald-500/20 border-2 border-teal-400/40' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}>
                                  <div className="relative shrink-0">
                                    {member.avatar_url ? (
                                      <img src={member.avatar_url} alt={member.username} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white/20" />
                                    ) : (
                                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white font-bold text-base sm:text-lg border-2 border-white/20">
                                        {member.username.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    {isMe && (
                                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center border-2 border-gray-900">
                                        <i className="ri-user-star-fill text-white text-xs"></i>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-semibold text-sm truncate ${isMe ? 'text-teal-200' : 'text-white'}`}>{member.username}</p>
                                    {isMe ? (
                                      <span className="text-xs text-teal-400 font-medium">あなた</span>
                                    ) : (
                                      <p className="text-xs text-white/40">{new Date(member.joined_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' })}参加</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* メンバー週間学習推移グラフ */}
                  {groupMembers.length > 0 && (
                    <MemberWeeklyTrendChart
                      groupId={selectedGroup.id}
                      groupName={selectedGroup.name}
                      memberUserIds={groupMembers.map(m => m.user_id)}
                    />
                  )}

                  {/* グループスケジュール */}
                  <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/10">
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h3 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                        <i className="ri-calendar-line text-emerald-400 text-lg sm:text-xl"></i>グループスケジュール
                      </h3>
                      {groupSchedules[selectedGroup.id] ? (
                        <button onClick={() => setOpenCalendarGroupId(selectedGroup.id)} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 cursor-pointer">
                          <i className="ri-calendar-check-line"></i>カレンダーを開く
                        </button>
                      ) : (
                        <button onClick={() => { setCalendarName(''); setShowCreateSchedule(selectedGroup.id); }} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 cursor-pointer">
                          <i className="ri-add-line"></i>作成
                        </button>
                      )}
                    </div>
                    {groupSchedules[selectedGroup.id] ? (
                      <div onClick={() => setOpenCalendarGroupId(selectedGroup.id)} className="flex items-center gap-4 p-4 sm:p-5 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-emerald-500/30 transition-all cursor-pointer group/sched">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-500/20 rounded-xl shrink-0 group-hover/sched:bg-emerald-500/30 transition-colors">
                          <i className="ri-calendar-check-line text-emerald-400 text-xl sm:text-2xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-bold text-white truncate group-hover/sched:text-emerald-300 transition-colors">{groupSchedules[selectedGroup.id].calendar_name}</p>
                          <p className="text-xs text-white/40 mt-0.5">クリックしてカレンダーを開く</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-white/30 text-xl group-hover/sched:text-emerald-400 transition-colors shrink-0"></i>
                      </div>
                    ) : (
                      <div className="p-5 sm:p-8 bg-white/5 border border-dashed border-white/20 rounded-2xl text-center">
                        <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mx-auto mb-3">
                          <i className="ri-calendar-2-line text-2xl text-white/40"></i>
                        </div>
                        <p className="text-white/50 text-sm mb-1 font-medium">スケジュールがまだありません</p>
                        <p className="text-white/30 text-xs mb-4">グループ共有カレンダーを作成して、メンバーと予定を共有しましょう</p>
                        <button onClick={() => { setCalendarName(''); setShowCreateSchedule(selectedGroup.id); }} className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold text-sm shadow-lg shadow-emerald-500/20 flex items-center gap-2 mx-auto cursor-pointer">
                          <i className="ri-add-line"></i>カレンダーを作成
                        </button>
                      </div>
                    )}
                  </div>

                  {/* グループの達成報告 */}
                  <div ref={achievementSectionRef} className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/10">
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h3 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                        <i className="ri-trophy-line text-emerald-400 text-lg sm:text-xl"></i>グループの達成報告
                      </h3>
                      <button onClick={() => setShowAchievementForm(true)} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 cursor-pointer">
                        <i className="ri-add-line"></i>投稿
                      </button>
                    </div>
                    <AchievementList groupId={selectedGroup.id} refreshKey={achievementRefreshKey} />
                  </div>
                </div>
              </FadeInSection>
            )}
          </div>

          <Footer />

          {/* モーダル群 */}
          {showCreateGroup && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
              <div className="w-full sm:max-w-lg bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
                <div className="sm:hidden flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-white/20 rounded-full"></div></div>
                <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2"><i className="ri-group-line text-emerald-400"></i>新しいグループ</h2>
                  <button onClick={() => setShowCreateGroup(false)} className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"><i className="ri-close-line text-xl"></i></button>
                </div>
                <form onSubmit={handleCreateGroup} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">グループ名 <span className="text-red-400">*</span></label>
                    <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-white/25" placeholder="例: 情報工学科 2024" required />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">説明</label>
                    <textarea value={newGroupDescription} onChange={(e) => setNewGroupDescription(e.target.value)} className="w-full min-h-[90px] sm:min-h-[100px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none placeholder-white/25" placeholder="グループの説明を入力..." rows={3} />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                    <button type="button" onClick={() => setShowCreateGroup(false)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 text-white/50 bg-white/5 rounded-xl hover:bg-white/10 transition-colors font-semibold text-sm cursor-pointer">キャンセル</button>
                    <button type="submit" className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-emerald-500/20 text-sm cursor-pointer">作成する</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showJoinGroup && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="w-full sm:max-w-md bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl">
                <div className="sm:hidden flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-white/20 rounded-full"></div></div>
                <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2"><i className="ri-login-box-line text-emerald-400"></i>グループに参加</h2>
                  <button onClick={() => setShowJoinGroup(false)} className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"><i className="ri-close-line text-xl"></i></button>
                </div>
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">招待コード</label>
                    <input type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent uppercase placeholder-white/25 font-mono tracking-widest" placeholder="例: ABC12345" />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                    <button onClick={() => setShowJoinGroup(false)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 text-white/50 bg-white/5 rounded-xl hover:bg-white/10 transition-colors font-semibold text-sm cursor-pointer">キャンセル</button>
                    <button onClick={joinGroup} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-emerald-500/20 text-sm cursor-pointer">参加する</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showCreateSchedule && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="w-full sm:max-w-md bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl">
                <div className="sm:hidden flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-white/20 rounded-full"></div></div>
                <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2"><i className="ri-calendar-line text-emerald-400"></i>カレンダー作成</h2>
                  <button onClick={() => setShowCreateSchedule(null)} className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"><i className="ri-close-line text-xl"></i></button>
                </div>
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">カレンダー名</label>
                    <input type="text" value={calendarName} onChange={(e) => setCalendarName(e.target.value)} className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-white/25" placeholder="例: 試験対策スケジュール" />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                    <button onClick={() => setShowCreateSchedule(null)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 text-white/50 bg-white/5 rounded-xl hover:bg-white/10 transition-colors font-semibold text-sm cursor-pointer">キャンセル</button>
                    <button onClick={() => showCreateSchedule && createSchedule(showCreateSchedule)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-emerald-500/20 text-sm cursor-pointer">作成する</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {openCalendarGroupId && groupSchedules[openCalendarGroupId] && (
            <GroupScheduleCalendar
              groupId={openCalendarGroupId}
              scheduleId={groupSchedules[openCalendarGroupId].id}
              calendarName={groupSchedules[openCalendarGroupId].calendar_name}
              onClose={() => setOpenCalendarGroupId(null)}
              onUpdateCalendarName={(newName) => {
                setGroupSchedules(prev => ({ ...prev, [openCalendarGroupId]: { ...prev[openCalendarGroupId], calendar_name: newName } }));
              }}
            />
          )}

          {showAchievementForm && (
            <AchievementForm
              defaultGroupId={selectedGroup?.id}
              onSuccess={() => {
                setShowAchievementForm(false);
                setAchievementRefreshKey(prev => prev + 1);
              }}
              onCancel={() => setShowAchievementForm(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
